import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SystemSettingService } from '@app/api/systemsettting.service';
import { TranslationService } from '@app/core/_base/layout/services/translation.service';
import { environment } from '@app/../environments/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface SystemSettingItem {
  id?: string;
  parent_id?: string;
  type?: string;
  name?: string;
  value?: string;
  description?: string;
  sort_order?: number;
  is_hidden?: boolean;
  page_location?: string;
  language?: string;
}

export interface DynamicSection {
  id?: string;
  parent_title?: string;
  type?: string;
  items: SystemSettingItem[];
}

export interface ContactCtaModel {
  title: string;
  subtitle: string;
  actions: SystemSettingItem[];
  otherItems: SystemSettingItem[];
}

@Component({
  selector: 'meu-about-us',
  templateUrl: './about-us.component.html',
  styleUrls: ['./about-us.component.scss'],
})
export class AboutUsComponent implements OnInit, OnDestroy {
  host = environment.backendhost;
  isLoading = false;
  private destroy$ = new Subject<void>();

  // Dynamic Company Info Section
  companyInfoSectionTitle = '';
  companyLogo = '';
  companyInfoItems: SystemSettingItem[] = [];

  // Dynamic Vision & Mission Section
  visionMissionSectionTitle = '';
  visionMissionItems: SystemSettingItem[] = [];

  // Dynamic Contact CTA Section
  contactCta: ContactCtaModel = {
    title: '',
    subtitle: '',
    actions: [],
    otherItems: [],
  };

  constructor(
    private systemSettingService: SystemSettingService,
    private translationService: TranslationService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    // Listen to active language changes
    this.translationService.language$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang) => {
        const selectedLang = lang || this.translationService.getSelectedLanguage() || 'vi';
        this.loadAboutUsData(selectedLang);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  resetData(): void {
    this.companyInfoSectionTitle = '';
    this.companyLogo = '';
    this.companyInfoItems = [];

    this.visionMissionSectionTitle = '';
    this.visionMissionItems = [];

    this.contactCta = {
      title: '',
      subtitle: '',
      actions: [],
      otherItems: [],
    };
  }

  loadAboutUsData(lang: string): void {
    this.resetData();
    this.isLoading = true;

    const currentLang = lang || 'vi';
    const filter = 'page_location==about_us,language==' + currentLang;

    this.systemSettingService.apiSystemSettingGet(filter, 'sort_order', 1, 50).subscribe({
      next: (res) => {
        let collection: any[] = [];
        if (res && res.success && res.data) {
          if (Array.isArray(res.data.collection)) {
            collection = res.data.collection;
          } else if (Array.isArray(res.data)) {
            collection = res.data;
          }
        }

        // Fallback between 'ch' and 'zh' if no items found
        if (collection.length === 0 && (currentLang === 'ch' || currentLang === 'zh')) {
          const fallbackLang = currentLang === 'ch' ? 'zh' : 'ch';
          const fallbackFilter = 'page_location==about_us,language==' + fallbackLang;
          this.systemSettingService.apiSystemSettingGet(fallbackFilter, 'sort_order', 1, 50).subscribe({
            next: (fbRes) => {
              if (fbRes && fbRes.success && fbRes.data) {
                if (Array.isArray(fbRes.data.collection)) {
                  collection = fbRes.data.collection;
                } else if (Array.isArray(fbRes.data)) {
                  collection = fbRes.data;
                }
              }
              this.applyDynamicData(collection);
            },
            error: () => {
              this.applyDynamicData(collection);
            },
          });
          return;
        }

        this.applyDynamicData(collection);
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private applyDynamicData(collection: any[]): void {
    // 1. Process COMPANY INFO (company_info)
    const compSection = this.extractSection(collection, 'company_info');
    this.companyInfoSectionTitle = compSection.parent_title || '';

    // Extract logo if present
    const logoItem = compSection.items.find((item) => {
      const name = (item.name || '').toLowerCase();
      return name === 'logo' || name === 'company_logo';
    });

    if (logoItem && logoItem.value) {
      this.companyLogo = this.formatImageUrl(logoItem.value);
    } else {
      this.companyLogo = './assets/media/logos/logo-bimparts-christmas.png';
    }

    // Filter out logo from info list so it renders in the logo box
    this.companyInfoItems = compSection.items.filter((item) => {
      const name = (item.name || '').toLowerCase();
      return name !== 'logo' && name !== 'company_logo';
    });

    // 2. Process VISION & MISSION (vision_mission)
    const vmSection = this.extractSection(collection, 'vision_mission');
    this.visionMissionSectionTitle = vmSection.parent_title || '';
    this.visionMissionItems = vmSection.items;

    // 3. Process CONTACT CTA (contact_cta)
    const ctaSection = this.extractSection(collection, 'contact_cta');
    const ctaItems = ctaSection.items;

    // Extract title & subtitle
    const titleItem = ctaItems.find((item) => (item.name || '').toLowerCase() === 'title');
    const subtitleItem = ctaItems.find(
      (item) => (item.name || '').toLowerCase() === 'subtitle' || (item.name || '').toLowerCase() === 'description'
    );

    this.contactCta.title = titleItem ? (titleItem.value || '') : (ctaSection.parent_title || '');
    this.contactCta.subtitle = subtitleItem ? (subtitleItem.value || '') : '';

    // Action buttons (phone, email, zalo, links, etc.)
    this.contactCta.actions = ctaItems.filter((item) => {
      const name = (item.name || '').toLowerCase();
      if (name === 'title' || name === 'subtitle' || name === 'description') return false;
      return true;
    });

    this.isLoading = false;
  }

  private extractSection(collection: any[], typeName: string): DynamicSection {
    const targetType = typeName.toLowerCase();
    const parent =
      collection.find(
        (s) => s.type && s.type.toLowerCase() === targetType && (!s.parent_id || s.parent_id === '' || s.parent_id === null)
      ) || collection.find((s) => s.type && s.type.toLowerCase() === targetType);

    const items: SystemSettingItem[] = [];

    if (parent && parent.children && Array.isArray(parent.children)) {
      parent.children
        .filter((c: any) => !c.is_hidden)
        .forEach((c: any) => {
          if (!items.some((item) => item.id === c.id)) {
            items.push(c);
          }
        });
    }

    collection.forEach((item) => {
      if (!item.is_hidden) {
        if (parent && item.id !== parent.id) {
          if (item.parent_id === parent.id || (item.type && item.type.toLowerCase() === targetType && item.parent_id)) {
            if (!items.some((c) => c.id === item.id)) {
              items.push(item);
            }
          }
        } else if (!parent && item.type && item.type.toLowerCase() === targetType) {
          if (!items.some((c) => c.id === item.id)) {
            items.push(item);
          }
        }
      }
    });

    items.sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));

    return {
      id: parent ? parent.id : '',
      parent_title: parent ? (parent.parent_title || '') : '',
      type: typeName,
      items: items,
    };
  }

  isPhone(name?: string, value?: string): boolean {
    const fieldName = (name || '').toLowerCase();
    if (
      fieldName.indexOf('phone') !== -1 ||
      fieldName.indexOf('hotline') !== -1 ||
      fieldName.indexOf('tel') !== -1 ||
      fieldName.indexOf('zalo') !== -1
    ) {
      return true;
    }
    const val = (value || '').trim();
    return /^[0-9+\s().-]{7,20}$/.test(val);
  }

  isEmail(name?: string, value?: string): boolean {
    const fieldName = (name || '').toLowerCase();
    if (fieldName.indexOf('email') !== -1 || fieldName.indexOf('mail') !== -1) {
      return true;
    }
    const val = (value || '').trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  }

  isUrl(value?: string): boolean {
    if (!value) return false;
    const val = value.trim().toLowerCase();
    return (val.indexOf('http://') === 0 || val.indexOf('https://') === 0) && !this.isImage('', val);
  }

  isImage(name?: string, value?: string): boolean {
    const fieldName = (name || '').toLowerCase();
    if (
      fieldName.indexOf('logo') !== -1 ||
      fieldName.indexOf('image') !== -1 ||
      fieldName.indexOf('background') !== -1 ||
      fieldName.indexOf('img') !== -1 ||
      fieldName.indexOf('banner') !== -1 ||
      fieldName.indexOf('avatar') !== -1 ||
      fieldName.indexOf('icon') !== -1
    ) {
      return true;
    }

    const val = (value || '').trim().toLowerCase();
    if (!val) return false;
    if (val.indexOf('/public/') === 0 || val.indexOf('public/') === 0) return true;
    if (val.indexOf('http://') === 0 || val.indexOf('https://') === 0 || val.indexOf('data:image/') === 0) {
      if (val.match(/\.(png|jpe?g|gif|webp|svg|ico)(\?.*)?$/i)) return true;
    }
    return false;
  }

  formatImageUrl(path?: string): string {
    if (!path) return '';
    const trimmed = path.trim();
    if (
      trimmed.indexOf('http://') === 0 ||
      trimmed.indexOf('https://') === 0 ||
      trimmed.indexOf('data:') === 0 ||
      trimmed.indexOf('blob:') === 0
    ) {
      return trimmed;
    }
    if (trimmed.indexOf('./assets/') === 0 || trimmed.indexOf('assets/') === 0) {
      return trimmed;
    }
    const host = (this.host || '').replace(/\/+$/, '');
    const cleanPath = trimmed.indexOf('/') === 0 ? trimmed : '/' + trimmed;
    return host + cleanPath;
  }

  getSafeContent(value?: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(value || '');
  }

  getVmIcon(name?: string): string {
    const n = (name || '').toLowerCase();
    if (n.indexOf('vision') !== -1 || n.indexOf('tam_nhin') !== -1) return 'fa-eye';
    if (n.indexOf('mission') !== -1 || n.indexOf('su_menh') !== -1) return 'fa-bullseye';
    if (n.indexOf('value') !== -1 || n.indexOf('gia_tri') !== -1) return 'fa-gem';
    if (n.indexOf('philosophy') !== -1 || n.indexOf('triet_ly') !== -1) return 'fa-lightbulb';
    if (n.indexOf('target') !== -1 || n.indexOf('goal') !== -1 || n.indexOf('muc_tieu') !== -1) return 'fa-flag';
    if (n.indexOf('award') !== -1 || n.indexOf('certificate') !== -1 || n.indexOf('chung_nhan') !== -1) return 'fa-certificate';
    return 'fa-check-circle';
  }

  getActionIcon(name?: string): string {
    const n = (name || '').toLowerCase();
    if (n.indexOf('phone') !== -1 || n.indexOf('hotline') !== -1 || n.indexOf('tel') !== -1) return 'fa-phone';
    if (n.indexOf('zalo') !== -1) return 'fa-comment-dots';
    if (n.indexOf('email') !== -1 || n.indexOf('mail') !== -1) return 'fa-envelope';
    if (n.indexOf('facebook') !== -1 || n.indexOf('fb') !== -1) return 'fa-facebook-f';
    if (n.indexOf('youtube') !== -1) return 'fa-youtube';
    return 'fa-external-link-alt';
  }

  getActionBtnClass(name?: string): string {
    const n = (name || '').toLowerCase();
    if (n.indexOf('phone') !== -1 || n.indexOf('hotline') !== -1 || n.indexOf('tel') !== -1) return 'btn-phone';
    if (n.indexOf('email') !== -1 || n.indexOf('mail') !== -1) return 'btn-email';
    return 'btn-email';
  }

  getCleanPhone(val?: string): string {
    if (!val) return '';
    return val.replace(/[^0-9+]/g, '');
  }
}
